import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { lobbyRoomsRequest, myTablesRequest } from '../../../store/lobby/lobbyActions';
import { selectTotalActiveUsersCount } from '../../../store/lobby/lobbySagas';
import { formatBigNumbers } from '../../../utils/helpers/numberFormat';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import { CarouselNav } from '../common/CarouselNav';
import { STAGE, useCarouselStage } from '../common/carouselStage';
import { RecentTablesBar } from '../common/RecentTablesBar';
import { LobbyQuickCards } from '../Lobby/LobbyQuickCards';
import { LobbyDesktopRooms } from './LobbyDesktopRooms';
import { LobbyRoomCarousel, LobbyRoomCarouselHandle } from './LobbyRoomCarousel';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const SETTLE_MS = 500;

// The blocks under the carousel nav on the stage (Figma 8526:39565): the VIP / Tournaments quick
// cards at 554 and the recent-tables bar at 659, both 362 wide.
const QUICK_CARDS_TOP = 554;
const RECENT_BAR_TOP = 659;
const EXTRAS_WIDTH = 362;
// Room around the front card for its glow, which the carousel would otherwise clip.
const CARD_GLOW_ROOM = 16;

type LobbyNavigationProp = NativeStackNavigationProp<RootStackParamList, 'Lobby'>;
type LobbyRoute = RouteProp<RootStackParamList, 'Lobby'>;

export const LobbyRoomSection = () => {
	const navigation = useNavigation<LobbyNavigationProp>();
	const { params } = useRoute<LobbyRoute>();
	const { t } = useTranslation();
	const carouselRef = useRef<LobbyRoomCarouselHandle>(null);
	const initialPage = params?.lobbyPage ?? 0;
	const activeIndexRef = useRef(initialPage);
	const [activeIndex, setActiveIndex] = useState(initialPage);
	const [dotIndex, setDotIndex] = useState(initialPage);
	const carouselProgress = useSharedValue(initialPage);
	const { isDesktop } = useDevice();
	const stage = useCarouselStage();
	const dispatch = useDispatch();
	const {
		items: rooms,
		vipRoom,
		loading,
		loaded,
		lastFetched,
	} = useSelector((s: RootState) => s.lobby);
	const playerLevel = useSelector((s: RootState) => s.auth.user?.level);
	const isLogged = useSelector(selectIsLogged);
	const activeUsersCount = useSelector(selectTotalActiveUsersCount);

	const { width, cardWidth, cardHeight } = stage;
	const glowRoom = stage.s(CARD_GLOW_ROOM);
	const sectionHeight = cardHeight + glowRoom * 2;
	const layoutKey = `${Math.round(width)}-${Math.round(cardHeight)}`;

	// Only write to navigation params when the page actually changed: every
	// setParams mutates navigation state and re-renders all its subscribers,
	// which is wasteful during swipe gestures and on mount.
	const lastPersistedPageRef = useRef<number>(initialPage);
	const persistPage = useCallback(
		(pageIndex: number) => {
			if (lastPersistedPageRef.current === pageIndex) return;
			lastPersistedPageRef.current = pageIndex;
			navigation.setParams({ lobbyPage: pageIndex });
		},
		[navigation],
	);

	const visibleRooms = useMemo(
		() =>
			rooms.filter(room => {
				return !(
					typeof playerLevel === 'number' &&
					Number.isFinite(playerLevel) &&
					typeof room.maxLevel === 'number' &&
					Number.isFinite(room.maxLevel) &&
					playerLevel > room.maxLevel
				);
			}),
		[playerLevel, rooms],
	);
	const visibleRoomsCount = visibleRooms.length;

	useEffect(() => {
		const CACHE_TTL = 5 * 60 * 1000;
		const isCacheValid =
			loaded && lastFetched !== null && Date.now() - lastFetched < CACHE_TTL && rooms.length > 0;

		if (!isCacheValid) {
			dispatch(lobbyRoomsRequest({ start: 0, limit: 20 }));
		}
	}, [dispatch, loaded, lastFetched, rooms.length]);

	useEffect(() => {
		if (isLogged) {
			dispatch(myTablesRequest());
		}
	}, [dispatch, isLogged]);

	useEffect(() => {
		activeIndexRef.current = activeIndex;
		persistPage(activeIndex);
	}, [activeIndex, persistPage]);

	useEffect(() => {
		const safeIndex = clamp(activeIndex, 0, Math.max(0, visibleRoomsCount - 1));

		if (safeIndex === activeIndex) return;

		activeIndexRef.current = safeIndex;
		setActiveIndex(safeIndex);
		setDotIndex(safeIndex);
		persistPage(safeIndex);
		carouselRef.current?.scrollToIndex(safeIndex);
	}, [activeIndex, persistPage, visibleRoomsCount]);

	// onSnapToItem lands while the settle animation is still running, and both
	// setActiveIndex and the setParams inside persistPage re-render every
	// navigation subscriber - measured as a single ~59ms frame right at the end
	// of each swipe. The carousel owns its own position, so nothing here is
	// needed until the motion has stopped; the ref is updated immediately for
	// anything reading it synchronously.
	const commitIndexTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const handleActiveIndexChange = useCallback(
		(nextIndex: number) => {
			activeIndexRef.current = nextIndex;
			setDotIndex(nextIndex);

			if (commitIndexTimerRef.current) clearTimeout(commitIndexTimerRef.current);
			commitIndexTimerRef.current = setTimeout(() => {
				setActiveIndex(nextIndex);
				persistPage(nextIndex);
			}, SETTLE_MS);
		},
		[persistPage],
	);

	useEffect(
		() => () => {
			if (commitIndexTimerRef.current) clearTimeout(commitIndexTimerRef.current);
		},
		[],
	);

	const handleDotPress = useCallback((index: number) => {
		setDotIndex(index);
		carouselRef.current?.scrollToIndex(index);
	}, []);

	const handleStep = useCallback(
		(direction: -1 | 1) => {
			if (visibleRoomsCount <= 1) return;

			const stepped = activeIndexRef.current + direction;

			setDotIndex(((stepped % visibleRoomsCount) + visibleRoomsCount) % visibleRoomsCount);

			if (direction === 1) carouselRef.current?.next();
			else carouselRef.current?.prev();
		},
		[visibleRoomsCount],
	);
	const shouldShowFullLoader = loading && rooms.length === 0;

	const handleRecentTablesPress = useCallback(() => {
		navigation.navigate(isLogged ? 'MyTables' : 'Login');
	}, [isLogged, navigation]);

	// The design badges both quick cards with a live head count. The VIP room reports its
	// own, tournaments have no per-feature count yet, so the lobby-wide figure stands in.
	const vipPlayersCount = vipRoom?.playersCount ?? 0;

	const renderRooms = () => {
		if (shouldShowFullLoader) {
			return (
				<View style={[styles.centeredFill, { height: sectionHeight }]}>
					<ActivityIndicator size='large' color='#B7C1D1' />
				</View>
			);
		}

		if (visibleRoomsCount === 0) {
			return (
				<View style={[styles.centeredFill, { height: sectionHeight }]}>
					<Text style={styles.emptyRoomsText}>
						{t('T_SITE_LOBBY_NO_ROOMS', { defaultValue: 'No rooms available' })}
					</Text>
				</View>
			);
		}

		return (
			<>
				<View nativeID='rn-spine-root-lobby-rooms' style={styles.sharedCanvasLayer} />
				<LobbyRoomCarousel
					key={layoutKey}
					ref={carouselRef}
					rooms={visibleRooms}
					activeIndex={activeIndex}
					onActiveIndexChange={handleActiveIndexChange}
					width={width}
					height={sectionHeight}
					cardWidth={cardWidth}
					cardHeight={cardHeight}
					progress={carouselProgress}
				/>
			</>
		);
	};

	// The desktop frame lays the rooms out flat instead of on the swipe wheel.
	if (isDesktop) {
		return <LobbyDesktopRooms rooms={visibleRooms} loading={shouldShowFullLoader} />;
	}

	return (
		<View style={styles.page} pointerEvents='box-none'>
			<View
				style={[styles.block, { top: stage.y(STAGE.cardTop) - glowRoom, height: sectionHeight }]}
			>
				{renderRooms()}
			</View>

			{!shouldShowFullLoader && visibleRoomsCount > 0 ? (
				<View style={[styles.block, styles.nav, { top: stage.y(STAGE.dotsTop) }]}>
					<CarouselNav
						count={visibleRoomsCount}
						activeIndex={dotIndex}
						progress={carouselProgress}
						onDotPress={handleDotPress}
						onStep={handleStep}
						hintLabel={t('T_SITE_LOBBY_SWIPE_HINT', {
							defaultValue: 'Swipe to explore rooms',
						})}
						scale={stage.scale}
					/>
				</View>
			) : null}

			<View style={[styles.block, { top: stage.y(QUICK_CARDS_TOP) }]}>
				<LobbyQuickCards
					width={stage.s(EXTRAS_WIDTH)}
					vip={{
						title: t('T_SITE_VIP', { defaultValue: 'VIP' }),
						meta: 'LEVEL 2 - 60%',
						playersLabel: formatBigNumbers(vipPlayersCount, { fractionDigits: 1 }),
						onPress: () => navigation.navigate('VipRooms'),
					}}
					tournaments={{
						title: t('T_SITE_HOME_TOURNAMENTS', { defaultValue: 'TOURNAMENTS' }),
						meta: 'in 04:12',
						playersLabel: formatBigNumbers(activeUsersCount, { fractionDigits: 1 }),
						onPress: () => navigation.navigate('Tournaments'),
					}}
				/>
			</View>

			<View style={[styles.block, { top: stage.y(RECENT_BAR_TOP) }]}>
				<RecentTablesBar width={stage.s(EXTRAS_WIDTH)} onPress={handleRecentTablesPress} />
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	page: {
		...StyleSheet.absoluteFillObject,
	},
	// Each block hangs at its stage y, centred across the screen.
	block: {
		position: 'absolute',
		left: 0,
		right: 0,
		alignItems: 'center',
	},
	centeredFill: {
		alignSelf: 'stretch',
		alignItems: 'center',
		justifyContent: 'center',
	},
	emptyRoomsText: {
		color: '#fff',
	},
	sharedCanvasLayer: {
		...StyleSheet.absoluteFill,
		zIndex: 0,
		pointerEvents: 'none',
	},
	nav: {
		zIndex: 5,
	},
});

export default LobbyRoomSection;
